import React, { useEffect, useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { Card, Loading, PageHeader, Badge } from "@/components/common";

const RANGES = [["today", "Hari ini"], ["yesterday", "Kemarin"], ["week", "Minggu ini"], ["month", "Bulan ini"], ["last_month", "Bulan lalu"], ["custom", "Custom"]];
const LINES = ["gross_sales", "discount", "net_sales", "cogs", "waste", "packaging", "delivery", "channel_fee", "employee", "rent", "utilities", "other"];
const NEG = new Set(["discount", "cogs", "waste", "packaging", "delivery", "channel_fee", "employee", "rent", "utilities", "other"]);
const COMP = [
  { key: "net_sales", label: "Penjualan Netto" },
  { key: "cogs", label: "HPP (Bahan)", neg: true },
  { key: "waste", label: "Waste / Rusak", neg: true },
  { key: "gross_profit", label: "Laba Kotor", strong: true },
  { key: "operating_expenses", label: "Biaya Operasional", neg: true },
  { key: "net_profit", label: "Laba / Rugi Bersih", total: true },
];

export default function BusinessUnitPnl() {
  const [range, setRange] = useState("month");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [unit, setUnit] = useState("all");
  const [d, setD] = useState(null);

  const params = useMemo(() => ({ range, date_from: range === "custom" ? from : undefined, date_to: range === "custom" ? to : undefined }), [range, from, to]);
  useEffect(() => { setD(null); api.get("/owner/pnl", params).then(setD).catch(() => setD({ units: [], consolidated: {}, labels: {}, range: {} })); }, [params]);

  const units = d?.units || [];
  const selected = unit === "all" ? { name: "Semua Unit Bisnis", ...(d?.consolidated || {}) } : units.find((u) => u.unit_id === unit);
  const val = (k) => selected?.[k] || 0;

  return (
    <>
      <PageHeader title="P&L Unit Bisnis" subtitle={d?.range?.from ? `${d.range.from.slice(0, 10)} → ${d.range.to.slice(0, 10)}` : "Store · Titip Pasar · Electric Bike · Online"} icon={TrendingUp} />
      <div className="flex flex-wrap gap-2 mb-3">
        {RANGES.map(([k, l]) => <button key={k} onClick={() => setRange(k)} data-testid={`pnl-range-${k}`} className={`px-3 h-9 rounded-full text-sm font-medium ${range === k ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
      </div>
      {range === "custom" && (
        <div className="flex gap-2 mb-3 max-w-md">
          <input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="YYYY-MM-DD" data-testid="pnl-from" className="flex-1 h-10 px-3 rounded-xl border border-slate-300" />
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="YYYY-MM-DD" data-testid="pnl-to" className="flex-1 h-10 px-3 rounded-xl border border-slate-300" />
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setUnit("all")} data-testid="pnl-unit-all" className={`px-3 h-9 rounded-full text-sm font-medium ${unit === "all" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600"}`}>SEMUA</button>
        {units.map((u) => <button key={u.unit_id} onClick={() => setUnit(u.unit_id)} data-testid={`pnl-unit-${u.unit_id}`} className={`px-3 h-9 rounded-full text-sm font-medium ${unit === u.unit_id ? "bg-slate-800 text-white" : u.net_profit < 0 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{u.name}</button>)}
      </div>

      {!d ? <Loading /> : selected ? (
        <>
          <Card className={`p-5 mb-4 border ${val("net_profit") < 0 ? "border-rose-300" : "border-emerald-200"}`} data-testid="pnl-summary">
            <div className="flex justify-between items-center mb-3"><h3 className="font-semibold text-slate-800">{selected.name}</h3><Badge className={val("net_profit") < 0 ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}>{val("net_profit") < 0 ? "RUGI" : "LABA"}</Badge></div>
            <div className="grid grid-cols-3 gap-4">
              <Kpi label="Penjualan netto" value={rupiah(val("net_sales"))} />
              <Kpi label="Laba kotor" value={rupiah(val("gross_profit"))} color="text-emerald-600" />
              <Kpi label="Laba / rugi bersih" value={rupiah(val("net_profit"))} color={val("net_profit") < 0 ? "text-rose-600" : "text-emerald-600"} />
            </div>
            <div className="text-xs text-slate-400 mt-3">{selected.orders || 0} order · rata-rata {rupiah(val("avg_transaction"))} · biaya operasional {rupiah(val("operating_expenses"))}</div>
          </Card>

          <Card className="p-5 mb-4">
            <h3 className="text-xs font-bold uppercase tracking-wide text-primary mb-3">Rincian Laba Rugi</h3>
            {LINES.map((k) => <KV key={k} k={d.labels?.[k] || k} v={`${NEG.has(k) && val(k) ? "- " : ""}${rupiah(val(k))}`} bold={k === "net_sales"} />)}
            <div className="border-t border-slate-200 my-2" />
            <KV k="Laba Kotor (netto − HPP − waste)" v={rupiah(val("gross_profit"))} bold />
            <KV k="Total Biaya Operasional" v={`- ${rupiah(val("operating_expenses"))}`} />
            <KV k="Laba / Rugi Bersih" v={rupiah(val("net_profit"))} bold />
          </Card>

          {unit === "all" && (
            <Card className="p-0" data-testid="pnl-compare">
              <h3 className="text-xs font-bold uppercase tracking-wide text-primary p-4 pb-2">Perbandingan Laba Rugi per Toko</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="text-left font-semibold text-slate-600 px-3 py-2 sticky left-0 bg-slate-100 z-10 min-w-[150px]">Pos</th>
                      {[...units, { unit_id: "__total__", name: "TOTAL", ...(d.consolidated || {}) }].map((u) => (
                        <th key={u.unit_id} className={`text-right font-semibold px-3 py-2 min-w-[130px] ${u.unit_id === "__total__" ? "bg-emerald-100 text-emerald-800" : "text-slate-700"}`}>{u.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COMP.map((r) => (
                      <tr key={r.key} className={`border-t border-slate-100 ${r.strong ? "bg-slate-50" : ""}`}>
                        <td className={`text-left px-3 py-2 sticky left-0 z-10 ${r.strong ? "bg-slate-50" : "bg-white"} ${r.strong || r.total ? "font-bold text-slate-800" : "text-slate-500"}`}>{r.label}</td>
                        {[...units, { unit_id: "__total__", name: "TOTAL", ...(d.consolidated || {}) }].map((u) => {
                          const v = u[r.key] || 0;
                          const color = r.total ? (v < 0 ? "text-rose-600" : "text-emerald-600") : r.neg && v ? "text-rose-600" : "text-slate-800";
                          return <td key={u.unit_id} className={`text-right px-3 py-2 ${color} ${r.strong || r.total ? "font-bold" : ""} ${u.unit_id === "__total__" ? "bg-emerald-50/50" : ""}`}>{r.neg && v ? "- " : ""}{rupiah(v)}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-slate-400 p-4 pt-2">Kolom TOTAL = gabungan semua toko. Tambah/ubah nama toko di menu Cabang; laporan tiap toko terpisah otomatis.</div>
            </Card>
          )}
        </>
      ) : null}
    </>
  );
}

function Kpi({ label, value, color }) { return (<div><div className="text-xs text-slate-400">{label}</div><div className={`text-lg font-bold mt-0.5 ${color || "text-slate-800"}`}>{value}</div></div>); }
const KV = ({ k, v, bold }) => (<div className={`flex justify-between py-1 text-sm ${bold ? "font-bold text-slate-800" : "text-slate-600"}`}><span>{k}</span><span>{v}</span></div>);
